/// Course To Steer (CTS) — the tidal-triangle vector calculation.
///
/// You want to make good a particular track over the ground. The tidal
/// stream pushes you sideways, so the heading you steer through the
/// water differs from the ground track. The classic one-hour
/// construction: from the start, lay off the tide vector (set + rate);
/// from the tide vector's end, swing an arc of radius = boat speed to
/// cut the ground-track line. That arc-to-track leg is the course to
/// steer; the start-to-cut length is the speed over the ground.
///
/// All directions are degrees True (0–360, north up). Convert the
/// resulting course to a compass course separately via
/// `@nav-games/compass`. Pure logic — no DOM, fully unit-tested.

const DEG = Math.PI / 180;

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

export interface CtsInput {
  /// Desired course over the ground (the track to make good), °T.
  groundTrackDeg: number;
  /// Boat speed through the water, knots.
  boatSpeedKts: number;
  /// Tidal stream direction — the way the stream flows TOWARD, °T.
  setDeg: number;
  /// Tidal stream rate, knots.
  rateKts: number;
}

export interface CtsResult {
  /// False when the across-track tide exceeds boat speed (you can't
  /// hold the track) or the inputs are degenerate. The numeric fields
  /// are still filled where meaningful but should not be shown as a
  /// solution.
  feasible: boolean;
  /// Heading to steer through the water, °T.
  courseToSteerDeg: number;
  /// Resulting speed made good along the ground track, knots.
  speedOverGroundKts: number;
  /// Signed offset (courseToSteer − groundTrack), normalised to
  /// (−180, 180]. Negative = steer to port of the track, positive = to
  /// starboard.
  offsetDeg: number;
  /// Tide components relative to the ground track, knots (for working
  /// display): across pushes you off track, along speeds/slows you.
  tideAcrossKts: number;
  tideAlongKts: number;
}

/// Signed angle a−b normalised to (−180, 180].
function signedDelta(a: number, b: number): number {
  const d = normalizeDeg(a - b);
  return d > 180 ? d - 360 : d;
}

export function courseToSteer(input: CtsInput): CtsResult {
  const { groundTrackDeg, boatSpeedKts, setDeg, rateKts } = input;
  // Angle of the tide relative to the desired track.
  const alpha = signedDelta(setDeg, groundTrackDeg) * DEG;
  const tideAcrossKts = rateKts * Math.sin(alpha);
  const tideAlongKts = rateKts * Math.cos(alpha);

  // The boat's across-track component must cancel the tide's, so:
  //   boatSpeed · sin(beta) = −tideAcross
  const sinBeta = boatSpeedKts > 0 ? -tideAcrossKts / boatSpeedKts : 2;
  const feasibleHeading = Math.abs(sinBeta) <= 1 && boatSpeedKts > 0;

  const beta = feasibleHeading ? Math.asin(sinBeta) : 0;
  const offsetDeg = beta / DEG;
  const courseToSteerDeg = normalizeDeg(groundTrackDeg + offsetDeg);
  const speedOverGroundKts = boatSpeedKts * Math.cos(beta) + tideAlongKts;

  return {
    feasible: feasibleHeading && speedOverGroundKts > 0,
    courseToSteerDeg,
    speedOverGroundKts,
    offsetDeg,
    tideAcrossKts,
    tideAlongKts,
  };
}
